import _extends from 'babel-runtime/helpers/extends';
import _Object$getPrototypeOf from 'babel-runtime/core-js/object/get-prototype-of';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';

/* eslint-disable react/require-default-props */

import React, { Children, Component } from 'react';
import NodeResolver from 'react-node-resolver';
import flushable from 'flushable';

import Portal from '@atlaskit/portal';
import { withAnalyticsEvents, withAnalyticsContext, createAndFireEvent } from '@atlaskit/analytics-next';
import { layers } from '@atlaskit/theme';
import { name as packageName, version as packageVersion } from '../../package.json';

import { Tooltip as StyledTooltip } from '../styled';
import Animation from './Animation';
import Position from './Position';

import { hoveredPayload, unhoveredPayload } from './utils/analytics-payloads';

var pendingHide = void 0;

var showTooltip = function showTooltip(fn, defaultDelay) {
  var isHidePending = pendingHide && pendingHide.pending();
  if (isHidePending) {
    pendingHide.flush();
  }
  var pendingShow = flushable(function () {
    return fn(isHidePending);
  }, isHidePending ? 0 : defaultDelay);
  return pendingShow.cancel;
};

var hideTooltip = function hideTooltip(fn, defaultDelay) {
  pendingHide = flushable(function (flushed) {
    return fn(flushed);
  }, defaultDelay);
  return pendingHide.cancel;
};

var Tooltip = function (_Component) {
  _inherits(Tooltip, _Component);

  function Tooltip() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, Tooltip);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = Tooltip.__proto__ || _Object$getPrototypeOf(Tooltip)).call.apply(_ref, [this].concat(args))), _this), _this.mouseCoordinates = null, _this.cancelPendingSetState = function () {}, _this.state = {
      immediatelyHide: false,
      immediatelyShow: false,
      isVisible: false,
      everBeenVisible: false
    }, _this.handleWindowScroll = function () {
      if (_this.state.isVisible) {
        _this.cancelPendingSetState();
        _this.setState({ isVisible: false, immediatelyHide: true });
      }
    }, _this.handleMouseClick = function () {
      if (_this.props.hideTooltipOnClick) {
        _this.cancelPendingSetState();
        _this.setState({ isVisible: false, immediatelyHide: true });
      }
    }, _this.handleMouseOver = function (e) {
      if (e.target === _this.wrapperRef) return;
      _this.cancelPendingSetState();
      if (Boolean(_this.props.content) && !_this.state.isVisible) {
        _this.cancelPendingSetState = showTooltip(function (immediatelyShow) {
          _this.setState({
            isVisible: true,
            everBeenVisible: true,
            immediatelyShow: immediatelyShow
          });
          if (_this.props.onShow) {
            _this.props.onShow();
          }
        }, _this.props.delay);
      }
    }, _this.handleMouseLeave = function (e) {
      if (e.target === _this.wrapperRef) return;
      _this.cancelPendingSetState();
      if (_this.state.isVisible) {
        _this.cancelPendingSetState = hideTooltip(function (immediatelyHide) {
          _this.setState({ isVisible: false, immediatelyHide: immediatelyHide });
          if (_this.props.onHide) {
            _this.props.onHide();
          }
        }, _this.props.delay);
      }
    }, _this.handleMouseMove = function (event) {
      _this.mouseCoordinates = {
        left: event.clientX,
        top: event.clientY
      };
    }, _temp), _possibleConstructorReturn(_this, _ret);
  } // set in mouseover/mouseout handlers


  _createClass(Tooltip, [{
    key: 'componentDidUpdate',
    value: function componentDidUpdate(prevProps, prevState) {
      var scrollOptions = { capture: true, passive: true };
      if (!prevState.isVisible && this.state.isVisible) {
        window.addEventListener('scroll', this.handleWindowScroll, scrollOptions);
      } else if (prevState.isVisible && !this.state.isVisible) {
        window.removeEventListener('scroll', this.handleWindowScroll, scrollOptions);
      }
    }

    // Update mouse coordinates, used when position is 'mouse'.
    // We are not debouncing/throttling this function because we aren't causing any
    // re-renders or performaing any intensive calculations, we're just updating a value.
    // React also doesn't play nice debounced DOM event handlers because they pool their
    // SyntheticEvent objects. Need to use event.persist as a workaround - https://stackoverflow.com/a/24679479/893630

  }, {
    key: 'render',
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          children = _props.children,
          content = _props.content,
          position = _props.position,
          mousePosition = _props.mousePosition,
          truncate = _props.truncate,
          TooltipContainer = _props.component,
          TargetContainer = _props.tag;
      var _state = this.state,
          isVisible = _state.isVisible,
          everBeenVisible = _state.everBeenVisible,
          immediatelyShow = _state.immediatelyShow,
          immediatelyHide = _state.immediatelyHide;

      return React.createElement(
        React.Fragment,
        null,
        React.createElement(
          TargetContainer,
          {
            onClick: this.handleMouseClick,
            onMouseOver: this.handleMouseOver,
            onMouseOut: this.handleMouseLeave,
            onMouseMove: this.handleMouseMove,
            ref: function ref(wrapperRef) {
              _this2.wrapperRef = wrapperRef;
            }
          },
          React.createElement(
            NodeResolver,
            {
              innerRef: function innerRef(targetRef) {
                _this2.targetRef = targetRef;
              }
            },
            Children.only(children)
          )
        ),
        everBeenVisible && React.createElement(
          Animation,
          {
            immediatelyShow: immediatelyShow,
            immediatelyHide: immediatelyHide,
            'in': isVisible
          },
          function (getAnimationStyles) {
            return React.createElement(
              Portal,
              { zIndex: layers.tooltip() },
              React.createElement(
                Position,
                {
                  key: position,
                  mouseCoordinates: _this2.mouseCoordinates,
                  mousePosition: mousePosition,
                  position: position,
                  target: _this2.targetRef
                },
                function (ref, placement, positionStyles) {
                  return React.createElement(
                    TooltipContainer,
                    {
                      innerRef: ref,
                      style: _extends({}, positionStyles, getAnimationStyles(placement)),
                      truncate: truncate,
                      'data-placement': placement
                    },
                    content
                  );
                }
              )
            );
          }
        )
      );
    }
  }]);

  return Tooltip;
}(Component);

Tooltip.defaultProps = {
  component: StyledTooltip,
  delay: 300,
  mousePosition: 'bottom',
  position: 'bottom',
  tag: 'div'
};


export { Tooltip as TooltipWithoutAnalytics };
var createAndFireEventOnAtlaskit = createAndFireEvent('atlaskit');

export default withAnalyticsContext({
  componentName: 'tooltip',
  packageName: packageName,
  packageVersion: packageVersion
})(withAnalyticsEvents({
  onHide: unhoveredPayload,
  onShow: createAndFireEventOnAtlaskit(_extends({}, hoveredPayload))
})(Tooltip));


//////////////////
// WEBPACK FOOTER
// ./node_modules/@atlaskit/tooltip/dist/esm/components/Tooltip.js
// module id = 657
// module chunks = 0